"use client";

import {
  type ReactElement,
  type RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { buildXYChartLayout } from "./layout-xychart";
import { parseMermaidXYChart } from "./parse-mermaid-xychart";
import { useXYChartDownloadActions } from "./useXYChartDownloadActions";
import { useXYChartViewportSize } from "./useXYChartViewportSize";
import { XYChartCompactPreview } from "./XYChartCompactPreview";
import { XYChartControls } from "./XYChartControls";
import {
  XYCHART_DEFAULT_ZOOM,
  XYCHART_MIN_HEIGHT,
  XYCHART_MIN_WIDTH,
  XYCHART_ZOOM_MAX,
  XYCHART_ZOOM_MIN,
  XYCHART_ZOOM_STEP,
} from "./xychart-constants";
import {
  createXYChartSvgId,
  resolveXYChartLayoutDimensions,
  selectXYChartChrome,
  selectXYChartHoverEntries,
  selectXYChartPopoverModel,
  type XYChartChromeModel,
  type XYChartPopoverModel,
} from "./xychart-component-model";
import type { XYChartHoverEntry } from "./XYChartHoverPopover";
import type { XYChartLayout, XYChartParsed } from "./xychart-types";

export interface UseCmarkdownXYChartModelInput {
  content: string;
}

export interface UseCmarkdownXYChartModelResult {
  parsed: XYChartParsed | null;
  chrome: XYChartChromeModel | null;
  layout: XYChartLayout | null;
  uniqueSvgId: string;
  viewportRef: RefObject<HTMLDivElement | null>;
  svgRef: RefObject<SVGSVGElement | null>;
  hoveredSeriesIndex: number | null;
  hoveredCategoryIndex: number | null;
  hoverEntries: XYChartHoverEntry[];
  popoverModel: XYChartPopoverModel | null;
  compactPreview: ReactElement | null;
  toolbarControls: ReactElement;
  modalControls: ReactElement;
  onHoverSeries: (seriesIndex: number | null) => void;
  onHoverCategory: (categoryIndex: number | null) => void;
}

export function useCmarkdownXYChartModel({
  content,
}: UseCmarkdownXYChartModelInput): UseCmarkdownXYChartModelResult {
  const parsed = useMemo(() => parseMermaidXYChart(content), [content]);
  const uniqueSvgId = useMemo(
    () => createXYChartSvgId({ content, parsed }),
    [content, parsed],
  );

  const [zoom, setZoom] = useState<number>(XYCHART_DEFAULT_ZOOM);
  const [hoveredSeriesIndex, setHoveredSeriesIndex] = useState<number | null>(
    null,
  );
  const [hoveredCategoryIndex, setHoveredCategoryIndex] = useState<
    number | null
  >(null);

  const { size: viewportSize, ref: viewportRef } = useXYChartViewportSize({
    width: XYCHART_MIN_WIDTH,
    height: XYCHART_MIN_HEIGHT,
  });
  const viewportSizeWidth = viewportSize.width;
  const viewportSizeHeight = viewportSize.height;
  const layout: XYChartLayout | null = useMemo(() => {
    if (parsed == null) return null;

    const { containerWidth, containerHeight } = resolveXYChartLayoutDimensions({
      viewportSize: {
        width: viewportSizeWidth,
        height: viewportSizeHeight,
      },
    });

    return buildXYChartLayout({
      parsed,
      containerWidth,
      containerHeight,
      zoom,
    });
  }, [parsed, viewportSizeWidth, viewportSizeHeight, zoom]);

  const svgRef = useRef<SVGSVGElement | null>(null);
  const { imageExportBusy, handleDownloadCsv, handleDownloadImage } =
    useXYChartDownloadActions({ parsed, svgRef });

  const handleZoomIn = useCallback((): void => {
    setZoom((previousZoom) =>
      Math.min(
        XYCHART_ZOOM_MAX,
        Number((previousZoom + XYCHART_ZOOM_STEP).toFixed(2)),
      ),
    );
  }, []);
  const handleZoomOut = useCallback((): void => {
    setZoom((previousZoom) =>
      Math.max(
        XYCHART_ZOOM_MIN,
        Number((previousZoom - XYCHART_ZOOM_STEP).toFixed(2)),
      ),
    );
  }, []);

  const chrome = useMemo(() => {
    if (parsed == null) return null;
    return selectXYChartChrome({ parsed });
  }, [parsed]);

  const hoverEntries = useMemo(() => {
    if (parsed == null) return [];
    return selectXYChartHoverEntries({ parsed, hoveredCategoryIndex });
  }, [parsed, hoveredCategoryIndex]);

  const popoverModel = useMemo(() => {
    if (parsed == null) return null;
    return selectXYChartPopoverModel({ parsed, layout, hoveredCategoryIndex });
  }, [parsed, layout, hoveredCategoryIndex]);

  const compactPreview = useMemo(() => {
    if (parsed == null) return null;
    return <XYChartCompactPreview parsed={parsed} chartIdentity={uniqueSvgId} />;
  }, [parsed, uniqueSvgId]);

  const toolbarControls = (
    <XYChartControls
      zoom={zoom}
      imageExportBusy={imageExportBusy}
      hasParsedChart={parsed != null}
      onZoomIn={handleZoomIn}
      onZoomOut={handleZoomOut}
      onDownloadCsv={handleDownloadCsv}
      onDownloadImage={handleDownloadImage}
    />
  );
  const modalControls = (
    <XYChartControls
      zoom={zoom}
      imageExportBusy={imageExportBusy}
      hasParsedChart={parsed != null}
      onZoomIn={handleZoomIn}
      onZoomOut={handleZoomOut}
      onDownloadCsv={handleDownloadCsv}
      onDownloadImage={handleDownloadImage}
    />
  );

  const handleHoverSeries = useCallback((seriesIndex: number | null): void => {
    setHoveredSeriesIndex(seriesIndex);
  }, []);
  const handleHoverCategory = useCallback(
    (categoryIndex: number | null): void => {
      setHoveredCategoryIndex(categoryIndex);
    },
    [],
  );

  return {
    parsed,
    chrome,
    layout,
    uniqueSvgId,
    viewportRef,
    svgRef,
    hoveredSeriesIndex,
    hoveredCategoryIndex,
    hoverEntries,
    popoverModel,
    compactPreview,
    toolbarControls,
    modalControls,
    onHoverSeries: handleHoverSeries,
    onHoverCategory: handleHoverCategory,
  };
}
